import { useTimeout, useViewportSize } from '@mantine/hooks';
import { uniqBy } from 'es-toolkit';
import { type AnimationDefinition, motion } from 'framer-motion';
import { type FC, useCallback, useEffect, useMemo } from 'react';
import { v4 } from 'uuid';
import { useShallow } from 'zustand/react/shallow';
import AnimatedText from '@/components/AnimatedText';
import { useHostContext } from '@/context/HostContext.tsx';
import { useCreatorStatus } from '@/hooks/useCreatorStatus';
import AgentSetupProgress from '@/pages/AgentTraining/components/AgentSetupProgress';
import QuestionCard from '@/pages/AgentTraining/components/QuestionCard';
import { createTrainingAnimationVariants } from '@/pages/AgentTraining/components/StepsLoader/StepsLoader.const';
import { StepLoaderAnimationType } from '@/pages/AgentTraining/components/StepsLoader/StepsLoader.enum';
import { MINI_EXPANDED_HEIGHT_PCT } from '@/presentation/creatorLayout.constants';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { useAgentTraining } from '@/store/agent-training';
import { AgentTrainingStep } from '@/types/agent-training';
import { cn } from '@/utils';
import { useStepsLoaderAnimator } from './StepsLoaderAnimator.hook.ts';

const GAP = 12;
const OVERLAY_RATIO = 0.85;
const CHROME_OFFSET = 230;
const VISIBLE_ROWS = 3;
const ROW_GAP = 20;

const StepsLoader: FC = () => {
	const { isMiniCreatorMode, isPageMode, isOverlayMode } = useHostContext();

	const {
		agentTrainingStep,
		scrapingSteps,
		setScrapingStep,
		setIsScrappingAnimationFinished,
		setIsScrappingSuccessAnimationFinished,
		isScrappingAnimationFinished,
		isScrappingSuccessAnimationFinished,
		isAgentProgressStepsVisible,
		setAgentProgressStepsVisible
	} = useAgentTraining(
		useShallow((s) => ({
			scrapingSteps: s.scrapingSteps,
			agentTrainingStep: s.agentTrainingStep,
			setScrapingStep: s.setScrapingStep,
			setIsScrappingAnimationFinished: s.setIsScrappingAnimationFinished,
			setIsScrappingSuccessAnimationFinished:
				s.setIsScrappingSuccessAnimationFinished,
			isScrappingAnimationFinished: s.isScrappingAnimationFinished,
			isScrappingSuccessAnimationFinished:
				s.isScrappingSuccessAnimationFinished,
			isAgentProgressStepsVisible: s.isAgentProgressStepsVisible,
			setAgentProgressStepsVisible: s.setAgentProgressStepsVisible
		}))
	);

	const uniqScrapingSteps = useMemo(
		() => uniqBy(scrapingSteps, (item) => item.id),
		[scrapingSteps]
	);

	const { start: enableProgressSteps } = useTimeout(() => {
		setAgentProgressStepsVisible(true);
	}, 15000);

	const animationVariants = createTrainingAnimationVariants();

	const {
		trainingStatusText,
		isShowFinishedAnimationAndText,
		isScrappingProcessStarted
	} = useCreatorStatus();

	const titleAnimationVariant = useMemo(() => {
		if (isScrappingProcessStarted && !isScrappingAnimationFinished) {
			return StepLoaderAnimationType.CENTER_TO_CORNER;
		}
		if (!isShowFinishedAnimationAndText && isScrappingAnimationFinished) {
			return StepLoaderAnimationType.CORNER_IDLE;
		}
		if (
			isShowFinishedAnimationAndText &&
			!isScrappingSuccessAnimationFinished
		) {
			return StepLoaderAnimationType.CORNER_TO_CENTER;
		}
		if (isShowFinishedAnimationAndText && isScrappingSuccessAnimationFinished) {
			return StepLoaderAnimationType.CENTER_LEFT_IDLE;
		}
		return StepLoaderAnimationType.CORNER_IDLE;
	}, [
		isScrappingProcessStarted,
		isScrappingAnimationFinished,
		isShowFinishedAnimationAndText,
		isScrappingSuccessAnimationFinished
	]);

	const { containerStyle, animateOnMountRef, cardRef, cols } =
		useStepsLoaderAnimator();

	const { height: viewportH, width: viewportW } = useViewportSize();

	const containerH = useMemo(() => {
		if (!viewportH) {
			return 0;
		}
		if (isMiniCreatorMode) {
			return Math.round(viewportH * MINI_EXPANDED_HEIGHT_PCT * 0.2);
		}
		if (isOverlayMode) {
			return Math.max(
				300,
				Math.round(viewportH * OVERLAY_RATIO - CHROME_OFFSET)
			);
		}
		return viewportW < 768
			? Math.round(viewportH * 0.5)
			: Math.max(500, Math.round(viewportH - CHROME_OFFSET));
	}, [isMiniCreatorMode, isOverlayMode, viewportH, viewportW]);

	const gridRowH = useMemo(() => {
		if (!containerH || isMiniCreatorMode) {
			return 0;
		}
		return Math.round(
			(containerH - 20 - (VISIBLE_ROWS - 1) * ROW_GAP) / VISIBLE_ROWS
		);
	}, [containerH, isMiniCreatorMode]);

	const handleAnimationComplete = useCallback(
		(variant: AnimationDefinition) => {
			if (variant === StepLoaderAnimationType.CENTER_TO_CORNER) {
				setIsScrappingAnimationFinished(true);
			}
			if (variant === StepLoaderAnimationType.CORNER_TO_CENTER) {
				setIsScrappingSuccessAnimationFinished(true);
			}
		},
		[setIsScrappingAnimationFinished, setIsScrappingSuccessAnimationFinished]
	);

	useEffect(() => {
		if (
			agentTrainingStep === AgentTrainingStep.Loading &&
			isShowFinishedAnimationAndText &&
			!isAgentProgressStepsVisible &&
			!isMiniCreatorMode
		) {
			enableProgressSteps();
		}
	}, [
		agentTrainingStep,
		isShowFinishedAnimationAndText,
		isAgentProgressStepsVisible,
		isMiniCreatorMode,
		enableProgressSteps
	]);

	useEffect(() => {
		const offNotification = agentTrainingRealtime.onNotifyScrapingStep(
			(args) => {
				setScrapingStep(
					{
						id: v4(),
						question: args.question ?? '',
						answer: args.answer ?? ''
					},
					isMiniCreatorMode ? 4 : VISIBLE_ROWS * cols,
					isMiniCreatorMode ? 1 : cols
				);
			}
		);

		return () => {
			offNotification();
		};
	}, [setScrapingStep, isMiniCreatorMode, cols]);

	return (
		<div
			className={cn(
				'w-full flex flex-col gap-[10px] sm:min-w-[360px] xl:min-w-[400px]',
				{
					'h-full': !isMiniCreatorMode,
					'px-2 flex-[2] min-h-0 overflow-hidden': isMiniCreatorMode
				}
			)}
			data-testid="creator-training-in-progress"
		>
			{isAgentProgressStepsVisible ? (
				<AgentSetupProgress />
			) : (
				<>
					{!isMiniCreatorMode && (
						<motion.div className="h-[40px] overflow-visible">
							<motion.span
								animate={titleAnimationVariant}
								className="w-auto z-[111] absolute"
								initial="centerIdle"
								onAnimationComplete={handleAnimationComplete}
								variants={animationVariants}
							>
								<AnimatedText
									className="!absolute w-[auto] z-[111]"
									label={trainingStatusText}
								/>
							</motion.span>
						</motion.div>
					)}

					<div
						className={cn('relative overflow-hidden w-full flex', {
							'pb-[20px]': !isMiniCreatorMode,
							'py-2': isMiniCreatorMode,
							'items-start justify-start': !isMiniCreatorMode,
							'items-center justify-start': isMiniCreatorMode
						})}
						style={{ height: containerH || undefined }}
					>
						<motion.div
							className={cn('w-full h-full', {
								'flex items-center': isMiniCreatorMode
							})}
							style={containerStyle}
						>
							<motion.div
								className={cn('w-full', {
									'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-[20px]':
										!isMiniCreatorMode,
									'flex flex-row items-center h-full gap-[12px]':
										isMiniCreatorMode
								})}
								layout="position"
								style={
									isMiniCreatorMode
										? undefined
										: { gridAutoRows: `${gridRowH}px` }
								}
								transition={{
									layout: { duration: 0.8, ease: 'easeInOut' }
								}}
							>
								{uniqScrapingSteps.map((step, index) => (
									<motion.div
										animate={{ opacity: 1, scale: 1 }}
										className={cn('shrink-0 snap-start bg-white shadow-lg', {
											'rounded-2xl flex flex-col justify-start items-center text-center overflow-hidden':
												!isMiniCreatorMode,
											'rounded-xl p-2 h-full': isMiniCreatorMode
										})}
										data-id={step.id}
										initial={{ opacity: 0, scale: 0.95 }}
										key={step.id}
										layout="position"
										ref={index === 0 ? cardRef : animateOnMountRef}
										style={
											isMiniCreatorMode
												? { flex: `0 0 calc((100% - 2*${GAP}px)/3)` }
												: undefined
										}
										transition={{
											type: 'spring',
											stiffness: 220,
											damping: 24
										}}
									>
										<QuestionCard
											isMiniCreatorMode={isMiniCreatorMode}
											isOverlayMode={isOverlayMode}
											isPageMode={isPageMode}
											step={step}
										/>
									</motion.div>
								))}
							</motion.div>
						</motion.div>

						{!isMiniCreatorMode && (
							<div
								className={cn(
									'absolute bottom-0 left-0 w-full h-[50%] pointer-events-none bg-gradient-to-t from-white/90 via-white/20 to-transparent rounded-[15px]',
									{
										'h-[100%] backdrop-blur-[2px] bg-gray/30':
											isShowFinishedAnimationAndText
									}
								)}
							/>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default StepsLoader;
